import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import toast, { Toaster } from "react-hot-toast";
import { FaCheck } from "react-icons/fa";
import Logo from "../../../common/assets/blue-logo.svg";
import {
  fetchPaymentLinkInfo,
  payPaymentLink,
  confirm3dsPaymentLink,
  PaymentLinkInfo,
} from "../services/paymentLinkService";

const CARD_OPTIONS = {
  style: {
    base: {
      color: "#12141d",
      fontFamily: '"Outfit", system-ui, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "15px",
      fontWeight: "400",
      "::placeholder": { color: "#94a3b8" },
    },
    invalid: { color: "#DC2626", iconColor: "#DC2626" },
  },
  hidePostalCode: true,
};

const formatEuros = (cents: number) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

const PaymentLink: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [info, setInfo] = useState<PaymentLinkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [cardComplete, setCardComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoadError("Lien invalide");
      setLoading(false);
      return;
    }
    fetchPaymentLinkInfo(token)
      .then(setInfo)
      .catch((err) => {
        const msg = err.response?.data?.message || "Lien invalide ou expiré";
        setLoadError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const canSubmit = !!stripe && !!elements && !!info && cardComplete && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !info || !token) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error("Formulaire non chargé, rafraîchissez la page");

      const cardPm = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name: `${info.firstName} ${info.lastName}`, email: info.email },
      });
      if (cardPm.error) throw new Error(cardPm.error.message || "CB refusée");

      let result = await payPaymentLink(token, {
        cardPaymentMethodId: cardPm.paymentMethod.id,
      });

      if (result.requires_action && result.payment_intent_client_secret) {
        const confirm = await stripe.confirmCardPayment(result.payment_intent_client_secret);
        if (confirm.error) {
          throw new Error(confirm.error.message || "Authentification échouée");
        }
        result = await confirm3dsPaymentLink(token, {
          paymentIntentId: result.payment_intent_id!,
        });
      }

      if (result.success) {
        // Lien de transformation marque blanche → personnalisation de l'app
        if (result.redirectUrl && result.redirectUrl.includes("/personalization/")) {
          toast.success("Paiement effectué !");
          navigate(`/personalization/${token}`);
          return;
        }
        toast.success("Paiement effectué !");
        // Recharger les infos pour passer en mode "succès"
        const updated = await fetchPaymentLinkInfo(token);
        setInfo(updated);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        throw new Error(result.message || "Erreur lors du paiement");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Erreur inconnue";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-[#5e6075] text-sm">Chargement…</div>
      </div>
    );
  }

  if (loadError || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="bg-[#f6f6f9] border border-[#E5E7EB] rounded-2xl p-10 max-w-md w-full text-center">
          <h1 className="text-2xl font-semibold text-[#12141d] mb-3 tracking-tight">
            Lien non valide
          </h1>
          <p className="text-[#5e6075] text-sm mb-6">
            {loadError || "Ce lien de paiement n'est plus utilisable."}
          </p>
          <a
            href="mailto:contact@fideliz.fr"
            className="text-[#0068FE] text-sm hover:underline"
          >
            contact@fideliz.fr
          </a>
        </div>
      </div>
    );
  }

  // État succès — affiché après paiement réussi ou si le pro revient sur un lien déjà payé
  if (info.status === "paid") {
    const paidDate = info.paidAt
      ? new Date(info.paidAt).toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : null;

    return (
      <div className="min-h-screen bg-white font-['Outfit',system-ui,sans-serif]">
        <Toaster position="top-center" />

        <header className="border-b border-[#E5E7EB]">
          <div className="max-w-2xl mx-auto px-6 py-5">
            <img src={Logo} alt="Fideliz" className="h-7 w-auto" />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dcfce7] mb-6">
              <FaCheck className="w-7 h-7 text-[#16A34A]" />
            </div>
            <div className="text-xs font-medium tracking-[0.16em] uppercase text-[#16A34A] mb-3">
              Paiement confirmé
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#12141d] tracking-tight leading-tight mb-3">
              Merci {info.firstName} !
            </h1>
            <p className="text-base text-[#5e6075] leading-relaxed">
              Votre règlement de{" "}
              <span className="text-[#12141d] font-semibold">
                {formatEuros(info.amountTtcCents)} € TTC
              </span>{" "}
              a bien été pris en compte
              {paidDate && (
                <>
                  {" "}
                  le <span className="text-[#12141d] font-medium">{paidDate}</span>
                </>
              )}
              .
            </p>
          </div>

          {/* Récap */}
          <div className="bg-[#f6f6f9] border border-[#E5E7EB] rounded-2xl p-6 mb-6">
            <div className="text-xs font-medium tracking-[0.16em] uppercase text-[#5e6075] mb-3">
              Récapitulatif
            </div>
            <p className="text-sm text-[#12141d] mb-4 leading-relaxed">{info.description}</p>
            <div className="space-y-1.5 text-sm pt-4 border-t border-[#E5E7EB]">
              <div className="flex justify-between">
                <span className="text-[#5e6075]">Montant HT</span>
                <span className="text-[#12141d] font-medium">
                  {formatEuros(info.amountHtCents)} €
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5e6075]">TVA 20 %</span>
                <span className="text-[#12141d] font-medium">
                  {formatEuros(info.vatCents)} €
                </span>
              </div>
              <div className="flex justify-between pt-2 mt-1 border-t border-[#E5E7EB]">
                <span className="text-[#12141d] font-semibold">Total réglé</span>
                <span className="text-[#12141d] font-bold text-lg tracking-tight">
                  {formatEuros(info.amountTtcCents)} € TTC
                </span>
              </div>
            </div>
          </div>

          <div className="text-center space-y-3">
            <a
              href="/login"
              className="inline-block px-8 py-3.5 bg-[#0068FE] text-white rounded-lg font-medium text-[15px] hover:bg-[#0052CC] transition-colors"
            >
              Accéder à mon espace
            </a>
            <p className="text-xs text-[#5e6075]">
              Une facture vous a été envoyée par email à{" "}
              <span className="text-[#12141d] font-medium">{info.email}</span>.
            </p>
          </div>
        </main>

        <footer className="max-w-2xl mx-auto px-6 pb-10">
          <p className="text-xs text-[#94a3b8] text-center">
            Une question ?{" "}
            <a href="mailto:contact@fideliz.fr" className="text-[#0068FE] hover:underline">
              contact@fideliz.fr
            </a>
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Outfit',system-ui,sans-serif]">
      <Toaster position="top-center" />

      <header className="border-b border-[#E5E7EB]">
        <div className="max-w-2xl mx-auto px-6 py-5">
          <img src={Logo} alt="Fideliz" className="h-7 w-auto" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        <div className="mb-10">
          <div className="text-xs font-medium tracking-[0.16em] uppercase text-[#0068FE] mb-3">
            Paiement
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#12141d] tracking-tight leading-tight mb-3">
            Demande de règlement
          </h1>
          <p className="text-base text-[#5e6075] leading-relaxed">
            Bonjour <span className="text-[#12141d] font-medium">{info.firstName}</span>,
            voici un lien de paiement pour{" "}
            <span className="text-[#12141d] font-medium">{info.opticianName}</span>.
          </p>
        </div>

        {/* Description */}
        <div className="bg-[#EEF9FF] border border-[#D4F1FF] rounded-2xl p-5 mb-4">
          <div className="text-xs font-medium tracking-[0.16em] uppercase text-[#0068FE] mb-2">
            Objet
          </div>
          <p className="text-sm text-[#12141d] leading-relaxed">{info.description}</p>
        </div>

        {/* Récap prix */}
        <div className="bg-[#f6f6f9] border border-[#E5E7EB] rounded-2xl p-6 mb-6">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#5e6075]">Montant HT</span>
              <span className="text-[#12141d] font-medium">
                {formatEuros(info.amountHtCents)} €
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5e6075]">TVA 20 %</span>
              <span className="text-[#12141d] font-medium">
                {formatEuros(info.vatCents)} €
              </span>
            </div>
          </div>
          <div className="pt-4 mt-3 border-t border-[#E5E7EB] flex justify-between items-baseline">
            <span className="text-base font-semibold text-[#12141d]">Total à régler</span>
            <span className="text-2xl font-bold text-[#12141d] tracking-tight">
              {formatEuros(info.amountTtcCents)}{" "}
              <span className="text-base font-medium">€ TTC</span>
            </span>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-[#f6f6f9] border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 space-y-6"
        >
          <div>
            <label className="block text-sm font-medium text-[#12141d] mb-2">
              Carte bancaire
            </label>
            <div className="px-4 py-3.5 border border-[#E5E7EB] rounded-lg bg-white focus-within:border-[#12141d] transition-colors">
              <CardElement options={CARD_OPTIONS} onChange={(e) => setCardComplete(e.complete)} />
            </div>
          </div>

          {submitError && (
            <div className="bg-[#fef2f2] border border-[#fecaca] text-[#DC2626] rounded-lg p-3 text-sm">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-4 bg-[#0068FE] text-white rounded-lg font-medium text-[15px] hover:bg-[#0052CC] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting
              ? "Paiement en cours…"
              : `Régler ${formatEuros(info.amountTtcCents)} € TTC`}
          </button>

          <p className="text-xs text-[#5e6075] text-center">
            Paiement sécurisé par Stripe. Vos données bancaires ne transitent pas par nos
            serveurs.
          </p>
        </form>
      </main>

      <footer className="max-w-2xl mx-auto px-6 pb-10">
        <p className="text-xs text-[#94a3b8] text-center">
          Une question ?{" "}
          <a href="mailto:contact@fideliz.fr" className="text-[#0068FE] hover:underline">
            contact@fideliz.fr
          </a>
        </p>
      </footer>
    </div>
  );
};

export default PaymentLink;
